// Extract Chinese-character tokens from arbitrary input text.
// CJK Unified Ideographs (incl. extensions A & common). 1–4 character chunks.
// Deduplicates while preserving first-seen order.

import { db, newId } from '../data/db';
import { toToneMark, toToneNum } from '../speech/pinyin';
import type { Card, Lesson, SemanticTag } from './cardSchema';

const CJK_CHAR = /[㐀-䶿一-鿿]/g;
// Whitespace, punctuation (incl. fullwidth CJK punctuation), and ASCII separators
// all act as word boundaries.
const CJK_GROUP = /[㐀-䶿一-鿿]+/g;

export function extractCharacters(text: string): string[] {
  const matches = text.match(CJK_CHAR) ?? [];
  return Array.from(new Set(matches));
}

// Token = a CJK run delimited by whitespace/punctuation. If a run is 1–4 chars
// (the common case for word lists like "媽媽 爸爸 妹妹 弟弟") we keep it whole.
// Longer runs are continuous prose; we deliberately do NOT greedy-chunk them
// into nonsense 4-grams ("今天天氣"). Instead we explode the run into single
// hanzi tokens — the parent can manually combine them via the tag UI later.
// A proper dictionary-driven segmenter is a follow-up (see plan §3).
export function extractTokens(text: string): string[] {
  const groups = text.match(CJK_GROUP) ?? [];
  const tokens: string[] = [];
  for (const g of groups) {
    if (g.length <= 4) tokens.push(g);
    else for (const c of g) tokens.push(c);
  }
  return Array.from(new Set(tokens));
}

export function extractPassages(text: string): string[] {
  // Anything with at least 4 consecutive CJK chars is "passage-like".
  return text
    .split(/[\n\r。！？；]+/)
    .map((s) => s.trim())
    .filter((s) => (s.match(CJK_CHAR) ?? []).length >= 4)
    .slice(0, 10);
}

export interface IngestPayload {
  title: string;
  zoneTheme: SemanticTag;
  source: 'paste' | 'ocr';
  rawText: string;
  // The parent can edit the auto-extracted tokens before saving.
  tokens: string[];
  englishByToken: Record<string, string>;
}

export async function saveLesson(payload: IngestPayload): Promise<Lesson> {
  const now = Date.now();
  const lesson: Lesson = {
    id: newId(),
    title: payload.title || 'Untitled lesson',
    source: payload.source,
    passages: extractPassages(payload.rawText),
    zoneTheme: payload.zoneTheme,
    createdAt: now,
  };
  await db.lessons.put(lesson);
  for (const tok of payload.tokens) {
    const card: Card = {
      id: newId(),
      hanzi: tok,
      pinyin: toToneMark(tok),
      pinyinNum: toToneNum(tok),
      english: payload.englishByToken[tok] ?? '',
      tag: payload.zoneTheme,
      lessonId: lesson.id,
      createdAt: now,
    };
    await db.cards.put(card);
  }
  return lesson;
}
